import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { FsConfirmProvider } from './shared/components/dialog';
import { Suspense, useEffect } from 'react';
import { Provider } from 'react-redux';
import { prefixer } from 'stylis';
import rtlPlugin from 'stylis-plugin-rtl';
import { RegionTypes } from './config/regions';
import { GTMCodes } from './helpers/getGTMCodeHelper';
import {
	identifyOpenPanel,
	markOpenPanelSettingsUnavailable,
	syncOpenPanelEnabled,
	trackOpenPanelScreenView,
} from './helpers/openPanelHelper';
import {
	identifyCustomerIo,
	markCustomerIoSettingsUnavailable,
	syncCustomerIoEnabled,
	trackCustomerIoScreenView,
} from './helpers/customerIoHelper';
import { useLocation } from 'react-router-dom';
import { getUser } from './services/authService';
import useCurrencyTextReplace from './helpers/currencyTextChangerHelper';
import { Store } from './redux/store';
import Router from './router/router';
import { LanguageService } from './services/languageService';
import { SystemSettingsService } from './services/systemSettingsService';
import { HelmetProvider } from 'react-helmet-async';
import { IS_SA } from './config/appConfig';
import { InvestorDetailProvider } from './context/investorDetailContext';

// Create rtl cache

export const App = () => {
	const languageService = new LanguageService();
	const systemSettingsService = new SystemSettingsService();
	const location = useLocation();

	const cacheRtl = createCache({
		key: 'muirtl',
		stylisPlugins: languageService.isArabic() ? [prefixer, rtlPlugin] : [],
	});

	useEffect(() => {
		document.body.classList.add(IS_SA ? `platform-sa` : 'platform-gl');
	}, []);

	useEffect(() => {
		if (!localStorage.getItem('region')) {
			localStorage.setItem('region', RegionTypes.Global);
		}
		languageService.handleWrongLanguage();
		systemSettingsService
			.getAllFront()
			.then((settings) => {
				// getAllFront swallows its own errors and also falls through on an
				// empty body, resolving undefined either way. That is not "disabled",
				// so syncOpenPanelEnabled ignores it — and nothing else would ever
				// resolve the flag in this tab.
				if (settings == null) {
					markOpenPanelSettingsUnavailable();
					markCustomerIoSettingsUnavailable();
					return;
				}
				// Fresh backend flag wins over the cached value used at boot;
				// re-identify in case the SDK only just initialized.
				syncOpenPanelEnabled(settings);
				identifyOpenPanel(getUser());
				syncCustomerIoEnabled(settings);
				identifyCustomerIo(getUser());
			})
			.catch(() => {
				markOpenPanelSettingsUnavailable();
				markCustomerIoSettingsUnavailable();
			});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Replaces the SDK's own screen-view tracking, which is off because it would
	// put the reset-password token in every event's `__path` (see initOpenPanel).
	useEffect(() => {
		trackOpenPanelScreenView();
		trackCustomerIoScreenView();
	}, [location.pathname]);

	const theme = createTheme({
		direction: languageService.isArabic() ? 'rtl' : 'ltr',
		palette: {
			primary: {
				main: '#33CD87',
			},
			secondary: {
				main: '#7850F2',
			},
		},
	});

	const isProd = () => process.env.REACT_APP_ENV === 'prod';

	const addGTMAndSegment = () => {
		const robotsMeta = document.createElement('meta');
		robotsMeta.name = 'robots';

		if (isProd()) {
			robotsMeta.content = 'index, follow';
		} else {
			robotsMeta.content = 'noindex, nofollow';
		}

		document.head.appendChild(robotsMeta);

		if (isProd()) {

			// Add Clarity Tracking Code
			const clarityTrackingCode = document.createElement('script');
			clarityTrackingCode.innerHTML = GTMCodes().clarityTrackingCode;
			document.head.appendChild(clarityTrackingCode);

			// GTM Script
			const gtmScript = document.createElement('script');
			gtmScript.innerHTML = GTMCodes().gtmScript;
			document.head.appendChild(gtmScript);

			// Segment Script
			const segmentScript = document.createElement('script');
			segmentScript.innerHTML = GTMCodes().segmentScript;
			document.head.appendChild(segmentScript);

			// Google Ads Script
			const gtagScript = document.createElement('script');
			gtagScript.src = GTMCodes().gtmUrl;
			document.head.appendChild(gtagScript);

			// Gtag Configuration
			const gtagAsync = document.createElement('script');
			gtagAsync.innerHTML = GTMCodes().gTagConfig;
			document.head.appendChild(gtagAsync);
		}
	};

	addGTMAndSegment();
	useCurrencyTextReplace(); // temp solution, create a new font file to handle the currency

	return (
		<HelmetProvider>
			<Provider store={Store}>
				<Suspense fallback={null}>
					<CacheProvider value={cacheRtl}>
						<ThemeProvider theme={theme}>
							<FsConfirmProvider>
								<InvestorDetailProvider>
									<main className="App">
										<Router></Router>
									</main>
								</InvestorDetailProvider>
							</FsConfirmProvider>
						</ThemeProvider>
					</CacheProvider>
				</Suspense>
			</Provider>
		</HelmetProvider>
	);
};
